// AllMsbForCommissionTransactions.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTentArrowLeftRight,
  faWallet,
  faEye,
  faArrowUp,
  faArrowDown,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

import { useNavigate } from 'react-router-dom';
import {jwtDecode} from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { formatDateIST } from '../../utils/dateFormat';

// Current week in IST: Monday .. today, as 'YYYY-MM-DD' (matches the backend's IST day boundaries)
const getIstWeekRange = () => {
  const ist = new Date(Date.now() + 5.5 * 60 * 60 * 1000); // read with UTC getters = IST wall clock
  const daysSinceMonday = (ist.getUTCDay() + 6) % 7;
  const monday = new Date(ist.getTime() - daysSinceMonday * 86400000);
  return { startDate: monday.toISOString().split('T')[0], endDate: ist.toISOString().split('T')[0] };
};

// Same Centre ID the reports show (qpayCentreId / msbId, else CTR-<last 6 of _id>)
const getCentreId = (u) => u.qpayCentreId || u.msbId || `CTR-${String(u._id || '').slice(-6).toUpperCase()}`;

const AllMsbForCommissionTransactions = () => {
  const [searchText, setSearchText] = useState('');
  const [msbUsers, setMsbUsers] = useState([]);
  const [commissionBalances, setCommissionBalances] = useState({});
  const [recentTransactions, setRecentTransactions] = useState({});
  const [loading, setLoading] = useState(true);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [userLevel, setUserLevel] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Get userId and userLevel from JWT token
  const token = sessionStorage.getItem('token');

  // Define fetchCommissionData first before using it in fetchMsbUsers
  const fetchCommissionData = useCallback(async (users) => {
    setBalancesLoading(true);
    const balances = {};
    const transactions = {};

    try {
      // Fetch commission balance and recent transactions for each MSB user
      const dataPromises = users.map(async (user) => {
        try {
          // Fetch balance - Using userId parameter
          const balanceResponse = await axios.get(
            `${API_BASE_URL}/msbCommission/balance/${user._id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (balanceResponse.status === 200 && balanceResponse.data.status === 'SUCCESS') {
            balances[user._id] = {
              totalBalance: balanceResponse.data.data.totalBalance || 0,
              lastUpdated: balanceResponse.data.data.lastUpdated,
              currency: balanceResponse.data.data.currency || '₹'
            };
          } else {
            balances[user._id] = { totalBalance: 0, currency: '₹' };
          }

          // Recent transactions: this week (IST Monday .. today)
          const week = getIstWeekRange();

          const transactionResponse = await axios.post(
            `${API_BASE_URL}/msbCommission/data/${user._id}`,
            {
              startDate: week.startDate,
              endDate: week.endDate,
              page: 1,
              limit: 10
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (transactionResponse.status === 200 && transactionResponse.data.status === 'SUCCESS') {
            const { summary, transactions: recentTxns } = transactionResponse.data.data;
            transactions[user._id] = {
              recentCount: recentTxns?.all?.length || 0,
              recentAdd: summary?.dateRange?.addCount || 0,
              recentWithdraw: summary?.dateRange?.withdrawCount || 0,
              recentAddAmount: summary?.dateRange?.totalAdd || 0,
              recentWithdrawAmount: summary?.dateRange?.totalWithdraw || 0
            };
          } else {
            transactions[user._id] = {
              recentCount: 0,
              recentAdd: 0,
              recentWithdraw: 0,
              recentAddAmount: 0,
              recentWithdrawAmount: 0
            };
          }
        } catch (error) {
          console.error(`Error fetching data for MSB user ${user._id}:`, error);
          balances[user._id] = { totalBalance: 0, currency: '₹' };
          transactions[user._id] = {
            recentCount: 0,
            recentAdd: 0,
            recentWithdraw: 0,
            recentAddAmount: 0,
            recentWithdrawAmount: 0
          };
        }
      });

      await Promise.all(dataPromises);
      setCommissionBalances(balances);
      setRecentTransactions(transactions);
    } catch (error) {
      console.error('Error fetching commission data:', error);
    } finally {
      setBalancesLoading(false);
    }
  }, [token]);

  const fetchMsbUsers = useCallback(async (currentUserId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/users/all/${currentUserId}?level=4&status=active`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setMsbUsers(response.data);
        // After fetching users, fetch their commission data
        if (response.data.length > 0) {
          fetchCommissionData(response.data);
        }
      } else {
        console.error('Failed to fetch MSB users');
        setError('Failed to fetch MSB users. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching MSB users:', error);
      if (error.response?.status === 403) {
        setError('You do not have permission to view MSB users.');
      } else if (error.response?.status === 404) {
        setError('No MSB users found.');
      } else {
        setError('An error occurred while fetching MSB users. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [token, fetchCommissionData]);

  useEffect(() => {
    if (!token) {
      setError('No authentication token found. Please login again.');
      setLoading(false);
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      setUserLevel(decodedToken.level);

      // Handle different user levels
      if (decodedToken.level === 5) {
        // Level 5 users are not allowed
        setError('You are not allowed to view this page.');
        setLoading(false);
        return;
      } else if (decodedToken.level === 4) {
        // Level 4 users (MSB) should go directly to their own commission transactions
        navigate(`/msbReport/commission-transactions/msb-commission-transactions/${decodedToken.userId}`);
        return;
      } else if (decodedToken.level <= 3) {
        // Level 1, 2, 3 users can see MSB list
        fetchMsbUsers(decodedToken.userId);
      } else {
        setError('Invalid user level.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error decoding token:', error);
      setError('Invalid authentication token. Please login again.');
      setLoading(false);
    }
  }, [token, navigate, fetchMsbUsers]);

  const handleViewCommissionTransactions = (msbUserId) => {
    navigate(`/msbReport/commission-transactions/msb-commission-transactions/${msbUserId}`);
  };

  const getUserLevelText = (level) => {
    switch(level) {
      case 1: return 'Super Admin';
      case 2: return 'State Admin';
      case 3: return 'State Admin Staff';
      case 4: return 'MSB';
      case 5: return 'MSB Staff';
      default: return 'Unknown';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getBalanceChipColor = (balance) => {
    if (balance >= 50000) return 'success';
    if (balance >= 10000) return 'warning';
    return 'default';
  };

  const getActivityLevel = (recent) => {
    const totalTransactions = recent.recentAdd + recent.recentWithdraw;
    if (totalTransactions >= 10) return { level: 'High', color: 'success' };
    if (totalTransactions >= 5) return { level: 'Medium', color: 'warning' };
    if (totalTransactions > 0) return { level: 'Low', color: 'info' };
    return { level: 'None', color: 'default' };
  };

  const filteredUsers = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return msbUsers;
    const qDigits = q.replace(/\D/g, '');
    return msbUsers.filter((u) => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
      const phone = String(u.phoneNumber || '').replace(/\D/g, '');
      return (
        getCentreId(u).toLowerCase().includes(q) ||
        name.includes(q) ||
        String(u.email || '').toLowerCase().includes(q) ||
        (qDigits && phone.includes(qDigits)) ||
        String(u.phoneNumber || '').toLowerCase().includes(q)
      );
    });
  }, [msbUsers, searchText]);

  // Show error state
  if (error) {
    return (
      <Box sx={{ position: 'relative', padding: 2 }}>
        <BackButton />
        <br /><br />
        <Card sx={{ maxWidth: 600, margin: '0 auto', mt: 4 }}>
          <CardContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6">Access Denied</Typography>
            </Alert>
            <Typography variant="body1" color="text.secondary">
              {error}
            </Typography>
            {userLevel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Your current level: {getUserLevelText(userLevel)}
              </Typography>
            )}
            {userLevel === 5 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                MSB Staff users cannot access commission reports directly. Please contact your MSB supervisor.
              </Typography>
            )}
            {userLevel === 4 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Redirecting you to your MSB commission transactions...
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Box sx={{ position: 'relative', padding: 2 }}>
        <BackButton />
        <br/> <br/>
        
        <Paper elevation={3} sx={{ p: 3, pr: 4, mb: 4, borderRadius: 3 }}>
          {/* Header with user level info */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
              <FontAwesomeIcon icon={faTentArrowLeftRight} style={{ marginRight: '10px', color: '#1976d2' }} />
              Q-PAY Centre Settlements Handle
            </Typography>
            <Chip 
              label={`Logged in as: ${getUserLevelText(userLevel)}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>

          {/* Info Alert */}
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Q-PAY Centre Settlements Handle:</strong> View detailed add/withdraw transaction history for each Q-PAY Centre. 
              Monitor commission balances, recent activity, and process withdrawals.
            </Typography>
          </Alert>

        {/* Summary Cards */}
        {!loading && msbUsers.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">
                    <FontAwesomeIcon icon={faUsers} style={{ marginRight: '8px' }} />
                    Total MSBs
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {msbUsers.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">
                    <FontAwesomeIcon icon={faWallet} style={{ marginRight: '8px' }} />
                    Combined Balance
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {formatCurrency(
                      Object.values(commissionBalances).reduce((sum, balance) => sum + (balance.totalBalance || 0), 0)
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="success.main">
                    <FontAwesomeIcon icon={faArrowUp} style={{ marginRight: '8px' }} />
                    <Tooltip title="Total commission credited to all listed Q-Pay Centres from Monday 00:00 IST to now (current week). Includes UPI and Cash commission credits; excludes withdrawals/settlements.">
                      <span>This Week Credits (Mon&ndash;Sun)</span>
                    </Tooltip>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Commission credited since Monday (IST)</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {formatCurrency(
                      Object.values(recentTransactions).reduce((sum, txn) => sum + (txn.recentAddAmount || 0), 0)
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="error.main">
                    <FontAwesomeIcon icon={faArrowDown} style={{ marginRight: '8px' }} />
                    <Tooltip title="Total withdrawn/settled from the Q-Pay Centres' commission balances from Monday 00:00 IST to now (current week).">
                      <span>This Week Debits (Mon&ndash;Sun)</span>
                    </Tooltip>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Withdrawals/settlements since Monday (IST)</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    {formatCurrency(
                      Object.values(recentTransactions).reduce((sum, txn) => sum + (txn.recentWithdrawAmount || 0), 0)
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by Centre ID, name, mobile or email"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 480 }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress color="primary" size={60} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading MSB users and commission transaction data...
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={3}>
            <Table aria-label="MSB Commission Transactions Table">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Sl.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>MSB Details</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Commission Balance</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Activity This Week (Mon–today)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user, index) => {
                  const balance = commissionBalances[user._id] || { totalBalance: 0, currency: '₹' };
                  const recent = recentTransactions[user._id] || {
                    recentCount: 0,
                    recentAdd: 0,
                    recentWithdraw: 0,
                    recentAddAmount: 0,
                    recentWithdrawAmount: 0
                  };
                  const activity = getActivityLevel(recent);
                  
                  return (
                    <TableRow 
                      key={user._id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f9f9f9' },
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                      }}
                    >
                      <TableCell>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }} display="block">
                            Centre ID: {getCentreId(user)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            📞 {user.phoneNumber}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ✉️ {user.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Pin Code: {user.pinCode}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FontAwesomeIcon icon={faWallet} style={{ color: '#1976d2' }} />
                            {balancesLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Chip
                                label={formatCurrency(balance.totalBalance)}
                                color={getBalanceChipColor(balance.totalBalance)}
                                variant="filled"
                                sx={{ fontWeight: 'bold', minWidth: 120 }}
                              />
                            )}
                          </Box>
                          {balance.lastUpdated && (
                            <Typography variant="caption" color="text.secondary">
                              Updated: {formatDateIST(balance.lastUpdated)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={`${activity.level} Activity`}
                              color={activity.color}
                              size="small"
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              ({recent.recentCount} txns)
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title={`Credits: ${formatCurrency(recent.recentAddAmount)}`}>
                              <Chip
                                icon={<FontAwesomeIcon icon={faArrowUp} />}
                                label={recent.recentAdd}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Tooltip>
                            <Tooltip title={`Debits: ${formatCurrency(recent.recentWithdrawAmount)}`}>
                              <Chip
                                icon={<FontAwesomeIcon icon={faArrowDown} />}
                                label={recent.recentWithdraw}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            </Tooltip>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleViewCommissionTransactions(user._id)}
                          startIcon={<FontAwesomeIcon icon={faTentArrowLeftRight} />}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                              transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.2s ease-in-out',
                            fontSize: '0.875rem',
                            minWidth: 200
                          }}
                        >
                          Manage Transactions
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <FontAwesomeIcon icon={faEye} size="3x" style={{ color: '#ccc', marginBottom: '16px' }} />
                        <Typography variant="h6" color="text.secondary">
                          {searchText.trim() ? 'No Q-Pay Centre matches your search.' : 'No MSB users found.'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {searchText.trim() ? 'Try a different Centre ID, name, mobile or email.' : 'There are currently no active MSB users in the system.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Footer Info */}
        {msbUsers.length > 0 && (
          <Box sx={{ mt: 3, p: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  📊 MSB Commission Transaction Overview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total MSBs: {msbUsers.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Combined Balance: {formatCurrency(
                    Object.values(commissionBalances).reduce((sum, balance) => sum + (balance.totalBalance || 0), 0)
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  📈 Weekly Activity Summary
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Credits: {formatCurrency(
                    Object.values(recentTransactions).reduce((sum, txn) => sum + (txn.recentAddAmount || 0), 0)
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Debits: {formatCurrency(
                    Object.values(recentTransactions).reduce((sum, txn) => sum + (txn.recentWithdrawAmount || 0), 0)
                  )}
                </Typography>
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                <strong>Usage Guide:</strong> Click "Manage Transactions" to view detailed commission transaction history for each MSB. 
                You can process withdrawals (if permitted), filter transactions by date/type, and export reports. 
                Activity and the weekly credit/debit totals cover the current week (Monday to today, IST).
              </Typography>
            </Alert>
          </Box>
        )}
        </Paper>
      </Box>
    </div>
  );
};

export default AllMsbForCommissionTransactions;